import { DEFAULT_RESEARCH_TYPE } from "../../../../../config/constants.ts";
import { Research, ResearchId } from "../../../state/AppState.ts";
import { HttpClient, Result } from "../../../../http/HttpClient.ts";

export class ResearchApiService {
  constructor(private readonly httpClient: HttpClient) {}

  async createResearch(payload: {
    knowledgeSourceId: string;
    researchGoal: string;
    additionalQuestions: string;
    researchType?: string;
  }): Result<{ id: string }> {
    const data: {
      knowledge_source: string;
      brief: string;
      questions: string;
      research_type: string;
    } = {
      knowledge_source: payload.knowledgeSourceId,
      brief: payload.researchGoal,
      questions: payload.additionalQuestions,
      research_type: payload.researchType || DEFAULT_RESEARCH_TYPE,
    };
    return this.httpClient.post<{ id: string }>("/research/", data);
  }

  async createChatFirstResearch(payload: {
    initialMessage: string;
    knowledgeSourceId?: string;
  }): Result<{ id: string }> {
    const data: {
      message: string;
      knowledge_source?: string;
    } = {
      message: payload.initialMessage,
    };
    if (payload.knowledgeSourceId) {
      data.knowledge_source = payload.knowledgeSourceId;
    }
    return this.httpClient.post<{ id: string }>("/research/chat-first", data);
  }

  async getResearch(payload: { id: string }): Result<Research> {
    return this.httpClient.get<{
      id: ResearchId;
      status: "pending" | "completed" | "processing_meta_analysis";
      knowledge_source: string;
      display_name: string;
      brief: string;
      questions: string;
      research_type?: string;
      create_time: string;
      update_time: string;
    }>(`/research/${payload.id}/`);
  }

  async renameResearch(payload: {
    researchId: ResearchId;
    name: string;
  }): Result<{ research: Research }> {
    const response = await this.httpClient.patch<{
      id: ResearchId;
      status: "pending" | "completed" | "processing_meta_analysis";
      knowledge_source: string;
      display_name: string;
      brief: string;
      questions: string;
      create_time: string;
      update_time: string;
    }>(`/research/${payload.researchId}/`, {
      display_name: payload.name,
    });
    return {
      success: response.success,
      data: response.data && {
        research: response.data,
      },
      httpStatus: response.httpStatus,
    };
  }

  async getResearchList(): Result<{ researches: Research[] }> {
    return this.httpClient.get<{
      researches: {
        id: ResearchId;
        display_name: string;
        status: "pending" | "completed" | "processing_meta_analysis";
        knowledge_source: string;
        brief: string;
        questions: string;
        research_type?: string;
        create_time: string;
        update_time: string;
      }[];
    }>("/research/");
  }

  async deleteResearch(payload: { researchId: string }): Result {
    const response = await this.httpClient.delete(
      `/research/${payload.researchId}/`,
    );
    return {
      success: response.success,
      httpStatus: response.httpStatus,
    };
  }

  async getResearchTypes(): Result<
    {
      name: string;
      description: string;
      display_name: string;
    }[]
  > {
    const response = await this.httpClient.get<
      {
        name: string;
        display_name: string;
        description: string;
      }[]
    >(`/research-types/`);
    return {
      success: response.success,
      data: response.data,
      httpStatus: response.httpStatus,
    };
  }
}
